import { useEffect, useRef } from 'react'
import { useRequest, getIntl, getLocale } from 'umi'
import type { RequestOptionsInit, ResponseError } from 'umi-request'
import Message from '@/components/Message'

export type SimpleRequestService = { url: string } & RequestOptionsInit
type RequestService = string | SimpleRequestService
type Service<P extends any[]> = RequestService | ((...args: P) => RequestService)

type IUseFetch = typeof useRequest

const defaultErrorHandler = (error: ResponseError<Error> | ResponseError<ServerError>) => {
  const intl = getIntl(getLocale())
  const message = intl.messages[`ApiErrorCode.${error.code}`] || error.message
  if (!message) {
    console.log('request error: ', error)
    return
  }
  Message.error(message)
}

const useFetch = (service: any, options: any = {}) => {
  const { isAutoAbort = true, ...rest } = options

  rest.throwOnError = true

  const CustomOnErrorHandler = rest.onError

  if (!rest.ignoreError) {
    rest.onError = !!CustomOnErrorHandler
      ? (error: ResponseError<Error>, params: any[]) => {
          error.defaultErrorHandler = () => defaultErrorHandler(error)
          CustomOnErrorHandler(error, params, () => defaultErrorHandler(error))
        }
      : defaultErrorHandler
  }

  const params = useRef<any>()

  const fn = (...args: any[]) => {
    if (typeof service === 'object') params.current = service
    else params.current = service(...args)
    return params.current
  }
  const result = useRequest(fn, rest)
  if (!isAutoAbort && params.current?.abort) {
    // @ts-ignore
    result.abort = params.current?.abort
  }
  useEffect(
    () => () => {
      result.cancel()
      if (isAutoAbort) params.current?.abort?.()
    },
    [],
  )
  return result
}

useFetch.AHOOKS_USE_REQUEST_DEFAULT_KEY = 'AHOOKS_USE_REQUEST_DEFAULT_KEY'

export type { RequestService, Service }

export default useFetch as IUseFetch & {
  AHOOKS_USE_REQUEST_DEFAULT_KEY: 'AHOOKS_USE_REQUEST_DEFAULT_KEY'
}
